/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Dec 19 2023
 **/

import { useEffect, useState } from "react";

import { Button, Column, Divider, Icon, Row, Typography } from "@/frameworks/designStuff/components";
import { useChatMessageActions } from "@/frameworks/core/chat";
import { useChat } from "@/frameworks/core/API";

import * as S from "./emotion";
import { ChatRenderer } from "@/frameworks/core/components";
import { usePreviewChatFile } from "../ChatItem/private";
import { Utility } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

function formatTimestamp(timestamp: number) {
	return new Date(timestamp).toLocaleTimeString(undefined, {
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		minute: "numeric"
	});
}

export default function ChatNotice({
	channelID,
	notice,
	variant
}: {
	channelID: string;
	notice: WebServices.Chat.GetRoom.Data["notice"];
	variant: ChatUI.Size;
}) {
	const [isOpened, setIsOpened] = useState(false);
	const [noticeUpdate, setNoticeUpdate] = useState<WebServices.Chat.GetRoom.Data["notice"]>(notice);
	const chatAPI = useChat({
		onSuccess(name, result) {
			if (result && result.data.roomId === channelID) {
				setNoticeUpdate(result.data.notice);
			}
		}
	});

	useEffect(() => {
		chatAPI.getRoom.execute({ roomID: channelID });
	}, [notice]);

	const { removeNotice } = useChatMessageActions();

	const { previewChatFile } = usePreviewChatFile();

	const clickMessage = (chatItem: ChatServices.Format2.Elements.AllRichtextElements) => {
		if (!notice) return;

		if (chatItem.type === "file" || chatItem.type === "image") {
			previewChatFile({
				files: noticeUpdate?.files ?? [],
				chatContent: chatItem,
				messageID: noticeUpdate?.message ?? "",
				roomID: channelID,
				userID: noticeUpdate?.userId ?? "",
				date: noticeUpdate?.timestamp,
				isHideFileSize: false
			});
		}
	};

	if (!notice) return;

	const isShowMessageNoticed =
		!isOpened &&
		noticeUpdate &&
		noticeUpdate.files &&
		noticeUpdate?.files?.length > 0 &&
		JSON.parse(noticeUpdate?.files?.[0]?.data || "")?.type !== "link";

	return (
		<S.NoticeLayout
			css={{ cursor: "pointer" }}
			gap={8}
			horizonAlign="distribute"
			isOpened={isOpened}
			variant={variant}
			onClick={() => {
				setIsOpened(!isOpened);
			}}
		>
			<Icon name="Notice24" size={16} />
			<Column css={{ position: "relative" }} className="notice_chat_renderer_wrapper" gap={8}>
				{isShowMessageNoticed ? (
					<Typography typoSize="Body3">{_T("파일이 공지로 등록되었습니다.")}</Typography>
				) : (
					<S.ChatContent isOpened={isOpened}>
						<ChatRenderer
							chatForm={
								(noticeUpdate
									? Utility.convert.stringToJSON(noticeUpdate.message)
									: "") as ChatServices.Format2.Message
							}
							files={noticeUpdate?.files}
							onClick={clickMessage}
						/>
					</S.ChatContent>
				)}
				{!isOpened && (
					<div
						className="notice_click_preventer"
						css={{ position: "absolute", width: "100%", height: "100%" }}
					/>
				)}
				{isOpened && (
					<>
						<Divider type="line" />
						<Row css={{ width: "100%" }} horizonAlign="distribute" verticalAlign="center">
							<Typography typoSize="Body4" typoColor="neutral_60_100">
								{noticeUpdate?.userName + " " + formatTimestamp(noticeUpdate?.timestamp || 0)}
							</Typography>
							<Button.Icon
								name="Delete24"
								size="medium"
								filled="transparent"
								fill="neutral_60_100"
								variant="contained"
								onClick={() => {
									removeNotice({ channelID: channelID });
								}}
							/>
						</Row>
					</>
				)}
			</Column>
			<Button.Icon
				size="small"
				name={variant === "transparent" ? (isOpened ? "Down24" : "Up24") : isOpened ? "Up24" : "Down24"}
				filled="transparent"
				variant="contained"
				css={{
					width: "16px",
					height: "16px"
				}}
			/>
		</S.NoticeLayout>
	);
}
